import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useMediaQuery } from '@material-ui/core';
import { ScrollHint } from '@this/shared/ui/feedbacks/scroll_hint';
import { breakpoints } from '@this/shared/ui/theme/tokens/primitive-tokens/breakpoints';
import SwapHorizIcon from '@material-ui/icons/SwapHoriz';
import TrendingFlatIcon from '@material-ui/icons/TrendingFlat';
import ShareIcon from '@material-ui/icons/Share';
import HotelIcon from '@material-ui/icons/Hotel';
import DirectionsCarIcon from '@material-ui/icons/DirectionsCar';

export type SearchTabIcon = 'roundTrip' | 'oneWay' | 'multipleLocations' | 'hotel' | 'rentalCar';

export interface SearchTypeTabItem {
  index: number;
  label: string;
  icon: SearchTabIcon;
  badge?: string;
}

interface Props {
  currentIndex: number;
  items: SearchTypeTabItem[];
  onChange: (index: number) => void;
}

const TAB_BUTTON_WIDTH = '84px';
const TAB_BUTTON_WIDTH_PX = 84;
const TAB_GAP_PX = 8;
const NARROW_VIEWPORT_MEDIA_QUERY = `(max-width: ${breakpoints[768]}px)`;

const ICON_MAP: Record<SearchTabIcon, React.ElementType> = {
  roundTrip: SwapHorizIcon,
  oneWay: TrendingFlatIcon,
  multipleLocations: ShareIcon,
  hotel: HotelIcon,
  rentalCar: DirectionsCarIcon
};

const getStyles = (isNarrowViewport: boolean, useCompactTabs: boolean) => ({
  scrollContainer: {
    flex: '5 1 0',
    minWidth: 0,
    overflow: isNarrowViewport ? 'visible' : 'hidden'
  } as React.CSSProperties,
  list: {
    display: 'flex',
    gap: '8px',
    flexWrap: 'nowrap'
  } as React.CSSProperties,
  button: (selected: boolean, hovered: boolean): React.CSSProperties => ({
    appearance: 'none',
    position: 'relative',
    display: 'inline-flex',
    flex: useCompactTabs ? `0 0 ${TAB_BUTTON_WIDTH}` : '1 1 0',
    width: useCompactTabs ? TAB_BUTTON_WIDTH : undefined,
    minWidth: useCompactTabs ? undefined : 0,
    boxSizing: 'border-box',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '5px',
    padding: '10px 6px',
    border: selected || hovered ? '1px solid var(--link-color, #af985e)' : '1px solid #d6dde8',
    borderRadius: '6px',
    background: selected || hovered ? 'var(--link-color-light, #eef5ff)' : 'white',
    color: selected || hovered ? 'var(--link-color, #af985e)' : '#404040',
    fontSize: '14px',
    fontWeight: selected ? 'bold' : 500,
    lineHeight: 1.2,
    whiteSpace: 'nowrap',
    cursor: 'pointer',
    transition: 'background-color 0.2s ease, border-color 0.2s ease, color 0.2s ease'
  }),
  icon: {
    display: 'inline-flex',
    width: '36px',
    height: '36px',
    alignItems: 'center',
    justifyContent: 'center',
    marginTop: '5px',
    fontSize: '36px'
  } as React.CSSProperties,
  label: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '100%',
    textAlign: 'center',
    lineHeight: 1.2,
    overflow: 'hidden',
    textOverflow: 'ellipsis'
  } as React.CSSProperties,
  badge: {
    position: 'absolute',
    top: '0.5px',
    right: '0.5px',
    padding: '2px 4px',
    background: '#e53935',
    color: 'white',
    fontSize: '9px',
    fontWeight: 'bold',
    lineHeight: 1.2,
    borderRadius: '4px',
    whiteSpace: 'nowrap',
    pointerEvents: 'none'
  } as React.CSSProperties
});

const SearchTypeTabs = ({ currentIndex, items, onChange }: Props) => {
  const buttonRefs = useRef<Array<HTMLButtonElement | null>>([]);
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
  const [tabsOverflow, setTabsOverflow] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const isNarrowViewport = useMediaQuery(NARROW_VIEWPORT_MEDIA_QUERY);
  const useCompactTabs = isNarrowViewport && tabsOverflow;
  const styles = useMemo(() => getStyles(isNarrowViewport, useCompactTabs), [isNarrowViewport, useCompactTabs]);
  const visibleItems = items;

  const updateTabsOverflow = useCallback(() => {
    if (!isNarrowViewport) {
      setTabsOverflow(false);
      return;
    }
    const element = containerRef.current;
    if (!element) {
      return;
    }
    const minContentWidth =
      visibleItems.length * TAB_BUTTON_WIDTH_PX + Math.max(visibleItems.length - 1, 0) * TAB_GAP_PX;
    setTabsOverflow(minContentWidth > element.clientWidth + 2);
  }, [isNarrowViewport, visibleItems.length]);

  useEffect(() => {
    const element = containerRef.current;
    if (!element) {
      return undefined;
    }
    updateTabsOverflow();
    const resizeObserver = new ResizeObserver(updateTabsOverflow);
    resizeObserver.observe(element);
    return () => resizeObserver.disconnect();
  }, [updateTabsOverflow]);

  const focusButton = (position: number) => {
    const next = (position + visibleItems.length) % visibleItems.length;
    buttonRefs.current[next]?.focus();
  };

  const handleKeyDown = (position: number, index: number) => (e: React.KeyboardEvent<HTMLButtonElement>) => {
    if (e.key === 'ArrowRight') {
      e.preventDefault();
      focusButton(position + 1);
    } else if (e.key === 'ArrowLeft') {
      e.preventDefault();
      focusButton(position - 1);
    } else if (e.key === ' ' || e.key === 'Enter') {
      e.preventDefault();
      onChange(index);
    }
  };

  return (
    <ScrollHint
      outerRef={containerRef}
      outerStyle={styles.scrollContainer}
      scrollStyle={styles.list}
      scrollProps={{ role: 'tablist', 'aria-label': '検索種別' }}
      showScrollControls={isNarrowViewport && tabsOverflow}
    >
      {visibleItems.map((item, position) => {
        const selected = currentIndex === item.index;
        return (
          <button
            type="button"
            key={item.index}
            ref={node => {
              buttonRefs.current[position] = node;
            }}
            role="tab"
            aria-selected={selected}
            tabIndex={selected ? 0 : -1}
            style={styles.button(selected, hoveredIndex === position)}
            onClick={() => onChange(item.index)}
            onKeyDown={handleKeyDown(position, item.index)}
            onMouseEnter={() => setHoveredIndex(position)}
            onMouseLeave={() => setHoveredIndex(null)}
          >
            {item.badge && <span style={styles.badge}>{item.badge}</span>}
            <span style={styles.icon} aria-hidden="true">
              <SearchTypeIcon icon={item.icon} />
            </span>
            <span style={styles.label}>{item.label}</span>
          </button>
        );
      })}
    </ScrollHint>
  );
};

const SearchTypeIcon = ({ icon }: { icon: SearchTabIcon }) => {
  const IconComponent = ICON_MAP[icon];
  return <IconComponent fontSize="inherit" />;
};

export default SearchTypeTabs;
